import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  ScrollArea,
  Group,
  ActionIcon,
  Text,
  UnstyledButton,
  Box,
} from "@mantine/core";
import {
  IconChevronUp,
  IconChevronDown,
  IconSelector,
} from "@tabler/icons-react";

const SortableTable = ({
  data = [],
  columns = [],
  onRowClick,
  highlightOnHover = true,
  style = {},
  className = "",
  scrollAreaProps = {},
  tableProps = {},
}) => {
  const [sorting, setSorting] = React.useState([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const SortButton = ({ column }) => {
    const sorted = column.getIsSorted();
    
    return (
      <UnstyledButton
        onClick={() => column.toggleSorting()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          width: "100%",
          padding: 0,
        }}
      >
        <Text weight={600} size="sm">
          {column.columnDef.header}
        </Text>
        <ActionIcon size="xs" variant="transparent">
          {sorted === "asc" ? (
            <IconChevronUp size={12} />
          ) : sorted === "desc" ? (
            <IconChevronDown size={12} />
          ) : (
            <IconSelector size={12} style={{ opacity: 0.5 }} />
          )}
        </ActionIcon>
      </UnstyledButton>
    );
  };

  return (
    <ScrollArea {...scrollAreaProps}>
      <Table
        highlightOnHover={highlightOnHover}
        style={{ marginBottom: "4rem", ...style }}
        className={className}
        {...tableProps}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    textAlign: header.column.columnDef.align || "left",
                    width: header.column.columnDef.width || "auto",
                    ...header.column.columnDef.headerStyle,
                  }}
                >
                  {header.isPlaceholder ? null : header.column.getCanSort() ? (
                    <SortButton column={header.column} />
                  ) : (
                    <Text weight={600} size="sm">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Text>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(row.original)}
              style={{
                cursor: onRowClick ? "pointer" : "default",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (highlightOnHover) {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                if (highlightOnHover) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={{
                    textAlign: cell.column.columnDef.align || "left",
                    ...cell.column.columnDef.cellStyle,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollArea>
  );
};

export default SortableTable; 